import {
  resolveMediaUrl,
  venuesApi,
  type VenueResponse,
  type VenueDetailResponse,
} from "@/lib/api";
import { type Venue, type VenueCategory, type FacilityType, type RoomType } from "@/data/venues";

/* ── Venue Service ───────────────────────────────────────────────
   Maps backend VenueResponse / VenueDetailResponse → frontend Venue model.
   BACKEND: See api.ts venuesApi for endpoint details.
   GET /api/v1/venues              → venuesApi.list()
   GET /api/v1/venues/:id          → venuesApi.getById()
   GET /api/v1/venues?managerId=   → venuesApi.myVenues()
   POST /api/v1/venues             → venuesApi.create()
   ─────────────────────────────────────────────────────────────── */

const FACILITY_TO_CATEGORY: Record<string, VenueCategory> = {
  Hotel: "hotel",
  Resort: "hotel",
  Apartment: "hotel",
  "Conference Hall": "event-centre",
  "Event Center": "event-centre",
  "Open Space": "open-space",
};

function mapRoomTypes(roomTypes?: VenueResponse["roomTypes"]): RoomType[] {
  return (
    roomTypes
      ?.filter((r) => r.isActive !== false)
      .map((r) => ({
        id: String(r.id),
        name: r.name,
        price: Number(r.pricePerDay),
        description: r.description ?? "",
        maxGuests: r.capacity,
        quantity: r.quantity ?? 1,
        images: (r.images ?? []).map((img) => resolveMediaUrl(img.imageUrl)).filter(Boolean),
        amenities: r.amenities ?? [],
      })) ?? []
  );
}

function parseCoordinate(value?: number | string): number | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/* Maps a backend list-item response to the frontend Venue model */
function mapListVenue(v: VenueResponse): Venue {
  const category: VenueCategory = FACILITY_TO_CATEGORY[v.type] ?? "hotel";
  const heroImage = resolveMediaUrl(v.heroImage);
  const galleryImages = v.gallery?.map((g) => resolveMediaUrl(g.imageUrl)).filter(Boolean) ?? [];
  const gallery = heroImage ? galleryImages.filter((url) => url !== heroImage) : galleryImages;
  const roomTypes = mapRoomTypes(v.roomTypes);
  return {
    id: String(v.id),
    name: v.name,
    category,
    facility: v.type as FacilityType,
    location: v.location,
    price: Number(v.basePrice),
    rating: Number(v.rating) || 0,
    reviews: Number(v.reviewsCount) || 0,
    bookingCount: Number(v.bookingCount) || 0,
    image: heroImage,
    gallery,
    description: v.description ?? v.fullDescription ?? "",
    amenities: v.highlights?.map((h) => h.name) ?? [],
    roomTypes: roomTypes.length > 0 ? roomTypes : undefined,
    capacity: v.capacity,
    bedrooms: v.bedrooms,
    latitude: parseCoordinate(v.latitude),
    longitude: parseCoordinate(v.longitude),
  };
}

/* Maps a backend detail response to the frontend Venue model */
function mapDetailVenue(v: VenueDetailResponse): Venue {
  const category: VenueCategory = FACILITY_TO_CATEGORY[v.type] ?? "hotel";
  const heroImage = resolveMediaUrl(v.heroImage);
  const gallery = v.gallery?.map((g) => resolveMediaUrl(g.imageUrl)).filter(Boolean) ?? [];
  const detailGallery = heroImage ? gallery.filter((url) => url !== heroImage) : gallery;
  const images = heroImage ? [heroImage, ...detailGallery] : gallery;
  const roomTypes = mapRoomTypes(v.roomTypes);
  return {
    id: String(v.id),
    name: v.name,
    category,
    facility: v.type as FacilityType,
    location: v.location,
    price: Number(v.basePrice),
    rating: Number(v.rating) || 0,
    reviews: Number(v.reviewsCount) || 0,
    bookingCount: Number(v.bookingCount) || 0,
    image: heroImage || images[0] || "",
    gallery: images,
    description: v.description ?? v.fullDescription ?? "",
    amenities: v.highlights?.map((h) => h.name) ?? [],
    roomTypes: roomTypes.length > 0 ? roomTypes : undefined,
    capacity: v.capacity,
    bedrooms: v.bedrooms,
    latitude: parseCoordinate(v.latitude),
    longitude: parseCoordinate(v.longitude),
    isFeatured: Number(v.rating) >= 4.0,
  };
}

export const VenueService = {
  async getVenues(filters?: {
    category?: VenueCategory;
    location?: string;
    facility?: string;
    priceRange?: { min: number; max: number };
  }): Promise<Venue[]> {
    const params: Record<string, string | number> = { pageSize: 100 };
    if (filters?.location) params.location = filters.location;
    if (filters?.facility) params.type = filters.facility;
    if (filters?.priceRange) {
      params.minPrice = filters.priceRange.min;
      if (isFinite(filters.priceRange.max)) params.maxPrice = filters.priceRange.max;
    }

    const res = await venuesApi.list({ ...params, moderationStatus: "approved" });
    let list = res.data
      .filter((v) => (v.moderationStatus ?? "pending").toLowerCase() === "approved")
      .map(mapListVenue);

    if (filters?.category && !filters.facility) {
      list = list.filter((v) => v.category === filters.category);
    }
    return list;
  },

  async getVenueById(id: string): Promise<Venue | null> {
    try {
      const v = await venuesApi.getById(id);
      return mapDetailVenue(v);
    } catch {
      return null;
    }
  },

  async getFeaturedVenues(): Promise<Venue[]> {
    const res = await venuesApi.list({
      pageSize: 20,
      sortBy: "rating",
      sortOrder: "desc",
      moderationStatus: "approved",
    });
    return res.data
      .filter((v) => (v.moderationStatus ?? "pending").toLowerCase() === "approved")
      .map(mapListVenue)
      .filter((v) => v.rating >= 4.0);
  },
};
